import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  UserIcon,
  LinkIcon,
  ChartBarIcon,
  ClockIcon,
  GlobeAltIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from 'recharts'
import { adminApiService } from '../../../shared/services/adminApi'
import toast from 'react-hot-toast'
import { formatDate as formatLocaleDate } from '../../../shared/lib/format'

interface UserDetail {
  _id: string
  email: string
  name?: string
  role: string
  isActive: boolean
  emailVerified?: boolean // Add email verification status
  plan: string
  createdAt: string
  lastLoginAt?: string
  stats: {
    totalUrls: number
    totalClicks: number
    avgCtr: number
    recentUrls: number
    recentClicks: number
  }
  recentLinks: Array<{
    id: string
    originalUrl: string
    shortCode: string
    clicks: number
    createdAt: string
  }>
}

interface UserUrls {
  urls: Array<{
    id: string
    originalUrl: string
    shortCode: string
    shortUrl: string
    title?: string
    clicks: number
    recentClicks: number
    isActive: boolean
    createdAt: string
    lastClickAt?: string
  }>
  pagination: {
    page: number
    limit: number
    totalUrls: number
    totalPages: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

interface UserAnalytics {
  timeRange: {
    startDate: string
    endDate: string
    days: number
  }
  clicksOverTime: Array<{
    date: string
    clicks: number
  }>
  deviceDistribution: Array<{
    name: string
    value: number
  }>
  countryDistribution: Array<{
    name: string
    value: number
  }>
  hourlyDistribution: Array<{
    hour: string
    clicks: number
  }>
  totalClicks: number
}

const COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
]

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()

  const [user, setUser] = useState<UserDetail | null>(null)
  const [analytics, setAnalytics] = useState<UserAnalytics | null>(null)
  const [userUrls, setUserUrls] = useState<UserUrls | null>(null)
  const [loading, setLoading] = useState(true)
  const [urlsLoading, setUrlsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'overview' | 'urls' | 'analytics'>(
    'overview'
  )

  useEffect(() => {
    if (!userId) {
      navigate('/users')
      return
    }
    loadUserData()
  }, [userId])

  const loadUserData = async () => {
    try {
      setLoading(true)

      console.log('Loading user data for userId:', userId) // Debug log

      // Load user details and analytics in parallel
      const [userDetail, userAnalytics] = await Promise.all([
        adminApiService.getUserDetails(userId!),
        adminApiService.getUserAnalytics(userId!, 30),
      ])

      console.log('User detail response:', userDetail) // Debug log
      console.log('User analytics response:', userAnalytics) // Debug log

      setUser(userDetail)
      setAnalytics(userAnalytics)
    } catch (error) {
      console.error('Failed to load user data:', error)
      toast.error('Failed to load user details')
    } finally {
      setLoading(false)
    }
  }

  const loadUserUrls = async (page: number = 1) => {
    if (!userId) {
      return
    }

    try {
      setUrlsLoading(true)
      const urlsData = await adminApiService.getUserUrls(userId, {
        page,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      })
      setUserUrls(urlsData)
    } catch (error) {
      console.error('Failed to load user URLs:', error)
      toast.error('Failed to load user URLs')
    } finally {
      setUrlsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return formatLocaleDate(dateString)
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/users')}
            className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="mr-1 h-4 w-4" />
            Back to Users
          </button>

          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl">
            User Details
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Detailed information and analytics for user account
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="py-12 text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">
              Loading user details...
            </p>
          </div>
        ) : !user ? (
          <div className="py-12 text-center">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              User not found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              The user you're looking for doesn't exist or has been removed.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* User Header */}
            <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-300 dark:bg-gray-600">
                    <UserIcon className="h-8 w-8 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {user.name || user.email.split('@')[0]}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {user.email}
                    </p>
                    <div className="mt-2 flex items-center space-x-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          user.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}
                      >
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                        {user.plan}
                      </span>
                      <div className="flex items-center">
                        {user.emailVerified ? (
                          <>
                            <CheckCircleIcon className="mr-1 h-4 w-4 text-green-500" />
                            <span className="text-xs text-green-700 dark:text-green-400">
                              Email Verified
                            </span>
                          </>
                        ) : (
                          <>
                            <XCircleIcon className="mr-1 h-4 w-4 text-red-500" />
                            <span className="text-xs text-red-700 dark:text-red-400">
                              Email Not Verified
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Member since
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatDate(user.createdAt)}
                  </p>
                  {user.lastLoginAt && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Last login
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatDate(user.lastLoginAt)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
                <div className="flex items-center">
                  <LinkIcon className="h-8 w-8 text-blue-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Total URLs
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatNumber(user.stats.totalUrls)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
                <div className="flex items-center">
                  <EyeIcon className="h-8 w-8 text-green-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Total Clicks
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatNumber(user.stats.totalClicks)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
                <div className="flex items-center">
                  <ChartBarIcon className="h-8 w-8 text-yellow-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Avg. CTR
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {user.stats.avgCtr.toFixed(2)}%
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
                <div className="flex items-center">
                  <ClockIcon className="h-8 w-8 text-purple-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Recent Activity
                    </p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {formatNumber(user.stats.recentClicks)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Last 30 days
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8 px-6">
                  {[
                    { key: 'overview', label: 'Overview', icon: ChartBarIcon },
                    { key: 'urls', label: 'URLs', icon: LinkIcon },
                    {
                      key: 'analytics',
                      label: 'Analytics',
                      icon: GlobeAltIcon,
                    },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key as any)}
                      className={`${
                        activeTab === tab.key
                          ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                      } flex items-center space-x-2 whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium`}
                      onClickCapture={() => {
                        if (tab.key === 'urls' && !userUrls) {
                          loadUserUrls()
                        }
                      }}
                    >
                      <tab.icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    {/* Recent URLs */}
                    <div>
                      <h4 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                        Recent URLs
                      </h4>
                      {user.recentLinks.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400">
                          No URLs created yet.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {user.recentLinks.map(link => (
                            <div
                              key={link.id}
                              className="flex items-center justify-between rounded-lg bg-gray-50 p-3 dark:bg-gray-700"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                                  {link.originalUrl}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  shorty.kr/{link.shortCode}
                                </p>
                              </div>
                              <div className="flex items-center space-x-4">
                                <div className="text-right">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {formatNumber(link.clicks)} clicks
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatDate(link.createdAt)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'analytics' && analytics && (
                  <div className="space-y-6">
                    {/* Clicks Over Time Chart */}
                    <div>
                      <h4 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                        Clicks Over Time (Last 30 Days)
                      </h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={analytics.clicksOverTime}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 12 }}
                              tickFormatter={value => formatDate(value)}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip
                              labelFormatter={value => formatDate(value)}
                            />
                            <Line
                              type="monotone"
                              dataKey="clicks"
                              stroke="#3B82F6"
                              strokeWidth={2}
                              dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Charts Grid */}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      {/* Device Distribution */}
                      <div>
                        <h4 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                          Device Distribution
                        </h4>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={analytics.deviceDistribution}
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                                label={({ name, percent }) =>
                                  `${name} ${(percent * 100).toFixed(0)}%`
                                }
                              >
                                {analytics.deviceDistribution.map(
                                  (_, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={COLORS[index % COLORS.length]}
                                    />
                                  )
                                )}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Country Distribution */}
                      <div>
                        <h4 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                          Top Countries
                        </h4>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={analytics.countryDistribution.slice(0, 5)}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                              <YAxis tick={{ fontSize: 12 }} />
                              <Tooltip />
                              <Bar dataKey="value" fill="#10B981" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>

                    {/* Hourly Activity */}
                    <div>
                      <h4 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                        Activity by Hour
                      </h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={analytics.hourlyDistribution}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip />
                            <Area
                              type="monotone"
                              dataKey="clicks"
                              stroke="#8B5CF6"
                              fill="#8B5CF6"
                              fillOpacity={0.3}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'urls' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                        All URLs
                      </h4>
                      {userUrls && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {userUrls.pagination.totalUrls} total URLs
                        </p>
                      )}
                    </div>

                    {urlsLoading ? (
                      <div className="py-8 text-center">
                        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
                        <p className="mt-2 text-gray-500 dark:text-gray-400">
                          Loading URLs...
                        </p>
                      </div>
                    ) : userUrls && userUrls.urls.length > 0 ? (
                      <div className="space-y-4">
                        {userUrls.urls.map(url => (
                          <div
                            key={url.id}
                            className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700"
                          >
                            <div className="flex items-start justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="mb-2 flex items-center space-x-2">
                                  <h5 className="truncate text-sm font-medium text-gray-900 dark:text-white">
                                    {url.title || url.originalUrl}
                                  </h5>
                                  <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                      url.isActive
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                                    }`}
                                  >
                                    {url.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                </div>

                                <p className="mb-1 truncate text-sm text-gray-600 dark:text-gray-300">
                                  Original: {url.originalUrl}
                                </p>

                                <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                                  <span className="rounded bg-blue-100 px-2 py-1 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                    {url.shortUrl}
                                  </span>
                                  <span>
                                    Created: {formatDate(url.createdAt)}
                                  </span>
                                  {url.lastClickAt && (
                                    <span>
                                      Last click: {formatDate(url.lastClickAt)}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="ml-4 flex flex-col items-end space-y-1">
                                <div className="text-right">
                                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {formatNumber(url.clicks)}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Total clicks
                                  </p>
                                </div>

                                {url.recentClicks > 0 && (
                                  <div className="text-right">
                                    <p className="text-sm font-medium text-green-600 dark:text-green-400">
                                      +{formatNumber(url.recentClicks)}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      Last 7 days
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Pagination */}
                        {userUrls.pagination.totalPages > 1 && (
                          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
                            <div className="flex flex-1 justify-between sm:hidden">
                              <button
                                onClick={() =>
                                  loadUserUrls(userUrls.pagination.page - 1)
                                }
                                disabled={!userUrls.pagination.hasPrevPage}
                                className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Previous
                              </button>
                              <button
                                onClick={() =>
                                  loadUserUrls(userUrls.pagination.page + 1)
                                }
                                disabled={!userUrls.pagination.hasNextPage}
                                className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Next
                              </button>
                            </div>

                            <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                              <div>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                  Showing page{' '}
                                  <span className="font-medium">
                                    {userUrls.pagination.page}
                                  </span>{' '}
                                  of{' '}
                                  <span className="font-medium">
                                    {userUrls.pagination.totalPages}
                                  </span>
                                </p>
                              </div>
                              <div>
                                <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                                  <button
                                    onClick={() =>
                                      loadUserUrls(userUrls.pagination.page - 1)
                                    }
                                    disabled={!userUrls.pagination.hasPrevPage}
                                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Previous
                                  </button>
                                  <button
                                    onClick={() =>
                                      loadUserUrls(userUrls.pagination.page + 1)
                                    }
                                    disabled={!userUrls.pagination.hasNextPage}
                                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Next
                                  </button>
                                </nav>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="py-8 text-center">
                        <LinkIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                          No URLs
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          This user hasn't created any URLs yet.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
